import { Component, OnInit, OnDestroy, Input, Inject } from '@angular/core';
import {
  SICallTypes,
  SICallTypesColorCode,
  SICallTypesDatabaseCode,
  ServiceInsightsCallData,
  ServiceMetricTypes,
  SICallTypesDisplayName,
  SITotalCallsByType,
  ServiceKpiCard,
  ServiceInsightsMetricCountsSummary,
} from './types/service-insight-types';
import { ServiceCallDistributionItem } from './components/service-call-distribution-chart/service-call-distribution-chart.component';
import { FiltersService } from 'src/app/core/services/filters.service';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { ServiceInsightsService } from './services/service-insights-service.service';
import { MixpanelService } from '../../core/services/mixpanel.service';
import { ContractModel } from 'src/app/global/contract-filter-model';
import { SpreadsheetService } from 'src/app/core/services/spreadsheet.service';
import {
  FEATURE_FLAGS,
  FeatureFlags,
} from 'src/app/config/AppInitializer.factory';
import { Division } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';

@Component({
    selector: 'app-service-insights',
    templateUrl: './service-insights.component.html',
    styleUrls: ['./service-insights.component.scss'],
    standalone: false
})
export class ServiceInsightsComponent implements OnInit, OnDestroy {
  SICallTypes = SICallTypes;
  isPestDivision: boolean = false;

  constructor(
    private _filterService: FiltersService,
    private _router: Router,
    private _serviceInsightsService: ServiceInsightsService,
    private mixpanelService: MixpanelService,
    private _spreadsheetService: SpreadsheetService,
    @Inject(FEATURE_FLAGS) private _featureFlags: FeatureFlags,
  ) {
    this._filterService.selectedFiltersObservable.subscribe((data) => {
      this.isPestDivision = data?.division === 'Pest';
    });
  }

  @Input() hideSpreadsheetDownload: boolean = true;

  downloadSpreadsheetStatus: string = '';

  spreadsheetDownloadStatus: Subscription | undefined;
  private distributionSubscription: Subscription | undefined;

  allChecked: boolean = true;

  filters: ContractModel | undefined;

  pageTitle: string = 'Service Insights';

  checkStatusSubscription: Subscription | undefined;

  loading: boolean = true;

  metrics: any[] = [];

    // Config-driven so additional Observation cards can be added without touching the template.
  observationMetrics: any[] = [
    { key: SICallTypes.PhotoObservations, title: SICallTypesDisplayName[SICallTypes.PhotoObservations], isEnableCheckBox: true },
    { key: SICallTypes.VirtualAssistCalls, title: SICallTypesDisplayName[SICallTypes.VirtualAssistCalls], isEnableCheckBox: false },
  ];

  serviceCallDistributionItems: ServiceCallDistributionItem[] = [];
  metricCountsSummary: ServiceInsightsMetricCountsSummary | undefined;

  getSummaryMetric(metricKey: SICallTypes) {
    return this.metricCountsSummary?.callTypeCounts?.[metricKey];
  }

  //expose enum for html
  ServiceMetricTypes = ServiceMetricTypes;

  private static readonly KPI_CARD_CONFIG: ReadonlyArray<Omit<ServiceKpiCard, 'value'>> = [
    { key: 'totalCalls',       title: 'Total Calls',               icon: 'neptune-icon-si-total-calls' },
    { key: 'totalLocations',   title: 'Total Locations',           icon: 'neptune-icon-si-location'    },
    { key: 'avgServiceperLoc', title: 'Avg Service Per Locations', icon: 'neptune-icon-si-location'    },
  ];

  serviceKpiCards: ServiceKpiCard[] = ServiceInsightsComponent.KPI_CARD_CONFIG.map(
    c => ({ ...c, value: 0 })
  );


  trackByKpiKey(index: number, kpi: ServiceKpiCard): string {
    return kpi.key;
  }

  async ngOnInit(): Promise<void> {
    this._serviceInsightsService.clearServiceCustomerFilters();
    this.spreadsheetDownloadStatus =
      this._spreadsheetService.downloadStatus$.subscribe((status) => {
        this.downloadSpreadsheetStatus =
          this._spreadsheetService.resolveSpreadsheetDownloadButtonText(
            status,
            this.filters?.division as Division,
          );
      });
    this.subscribe();
    await this.load();
  }

  subscribe() {
    this.checkStatusSubscription = this._serviceInsightsService?.isUnSelect.subscribe(
      unCheck => this.allChecked = !unCheck
    );

    // Get the filters selected
    this._filterService.selectedFiltersObservable.subscribe(f => this.filters = f);

    this.distributionSubscription = this._serviceInsightsService.getTotalCallsByTypeData().subscribe(
      data => {
        this.serviceCallDistributionItems = this._buildDistributionItems(data);
      }
    );
  }

  async load() {
    this.loading = true;

    if (this.filters == undefined) {
      alert('Please apply filters before navigating to Service Insights');
      this._router.navigate(['/filter']);
    } else {
      this.mixpanelService.initializeActiveDurationTracking(); // start active duration tracking
      this.loading = true;

      this._serviceInsightsService.resetServiceInsightsCallData();

      this._setMetrics();
      await this._getData();

      this.loading = false;
      // Push metrics to mixpanel
      this.mixpanelService.track('Service Insights Loaded', {
        "Page Name": this.pageTitle
      });
      this._serviceInsightsService.isServiceInsightsLoaded = true;
    }
  }

  ngOnDestroy() {
    this.spreadsheetDownloadStatus?.unsubscribe();
    this.distributionSubscription?.unsubscribe();
    this.mixpanelService.stopTrackingActiveTime();
    this.mixpanelService.trackPageView(this.pageTitle); //send page view event, which includes Page Name and Active Duration
  }

  _onAllCheckedChange(checked: boolean) {
    this.allChecked = checked;
    this._serviceInsightsService?.affectAllMetricsToPresentation(this.allChecked);
  }

  async _getData() {
    if (this.filters !== undefined) {
      this._onAllCheckedChange(true);

      if (!this.isPestDivision) {
        const advancedFilters = this._serviceInsightsService.serviceCustomerFilters.getValue();
        // TODO: getTotalCallsByTypeFromAPI (by-type totals) now only feeds serviceCallDistributionItems (the stacked bar chart).
        // Remove this call once the chart is migrated to metricCountsSummary.callTypeCounts.
        // Fetched in parallel since neither call depends on the other's result.
        const [result, summary] = await Promise.all([
          this._serviceInsightsService.getTotalCallsByTypeFromAPI(
            ServiceMetricTypes.Calls,
            advancedFilters
          ),
          this._serviceInsightsService.getMetricCountsSummaryFromAPI(),
        ]);

        if (result) {
          this._serviceInsightsService.setTotalCallsByTypeData(result);
        }
        this.metricCountsSummary = summary;
        this._updateKpiCardsFromSummary(this.metricCountsSummary);
      }
    }
  }

  async _setMetrics() {
    // For QSR and FRS divisions, the metric displayed in the UI is either Program Reviews or Audits depending on the state of the corresponding feature flags.
    // To avoid having multiple conditionals in the HTML template, we determine here which metric to display and map it to the Program Reviews key, so that the HTML can remain unchanged.
    const programOrAuditMetricType: keyof ServiceInsightsCallData =
  ((this.filters?.division === 'QSR' &&  this._featureFlags.enableAuditMetricForQSR) ||
   (this.filters?.division === 'FRS' && this._featureFlags.enableAuditMetricForFRS))
    ? SICallTypes.Audit
    : SICallTypes.ProgramReviews;
    // Display order: Extra Service Visits, Regular Service Calls, Program Reviews/Audits, Training Sessions, Quick Calls, Installations
    const keys: (keyof ServiceInsightsCallData)[] = [SICallTypes.ExtraServiceVisit, SICallTypes.RegularServiceCalls, programOrAuditMetricType, SICallTypes.TrainingSessions, SICallTypes.QuickCalls, SICallTypes.Installations];
    this.metrics = keys.map((key) => {
      const title = SICallTypesDisplayName[key];
      if (title) {
        return {
          key: key,
          title: title,
          borderColor: SICallTypesColorCode[key]
        }
      }
      return null
    }).filter((item:any) => (item != null) || (item && item.inputData.length != 0));
  }

  /** Derives Total Calls, Total Locations and Avg Service Per Location directly from the
   *  metric-counts-summary API's TotalCalls entry and pushes the values into serviceKpiCards.
   */
  private _updateKpiCardsFromSummary(summary: ServiceInsightsMetricCountsSummary | undefined): void {
    const totalCalls = summary?.callTypeCounts?.[SICallTypes.TotalCalls];

    const valueMap: Record<string, number> = {
      totalCalls: totalCalls?.total ?? 0,
      totalLocations: totalCalls?.locations ?? 0,
      avgServiceperLoc: totalCalls?.avgPerLocation ?? 0
    };

    this.serviceKpiCards = ServiceInsightsComponent.KPI_CARD_CONFIG.map(c => ({
      ...c,
      value: valueMap[c.key] ?? 0
    }));
  }

  /** Maps TotalCallsInsightModel (keyed by DB codes like "ESR") to ServiceCallDistributionItem[]. */
  private _buildDistributionItems(data: SITotalCallsByType): ServiceCallDistributionItem[] {
    const programOrAuditMetricType =
      ((this.filters?.division === 'QSR' && this._featureFlags.enableAuditMetricForQSR) ||
       (this.filters?.division === 'FRS' && this._featureFlags.enableAuditMetricForFRS))
        ? SICallTypes.Audit : SICallTypes.ProgramReviews;

    const relevantTypes: SICallTypes[] = [
      SICallTypes.ExtraServiceVisit,
      SICallTypes.RegularServiceCalls,
      programOrAuditMetricType,
      SICallTypes.Installations,
      SICallTypes.TrainingSessions,
      SICallTypes.QuickCalls,
    ];

    const dbCodeToConfig: Record<string, { key: SICallTypes; label: string; color: string }> =
      Object.fromEntries(
        relevantTypes.map(type => [
          SICallTypesDatabaseCode[type],
          { key: type, label: SICallTypesDisplayName[type], color: SICallTypesColorCode[type] }
        ])
      );

    return data.totalCalls
      .filter(item => dbCodeToConfig[item.callReason] != null)
      .map(({ callReason, total, totalLocations }) => ({
        key:            dbCodeToConfig[callReason].key,
        label:          dbCodeToConfig[callReason].label,
        value:          total,
        totalLocations,
        color:          dbCodeToConfig[callReason].color,
      }));
  }
}
